import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { AddIcon, DeleteIcon, DescriptionIcon } from 'components/Icons'
import { openFile } from 'utils/file'
import * as snackbar from 'utils/snackbar'

const MB = 1024 * 1024

export default function MultiFileUpload({
  items = [],
  onAdd,
  onRemove,
  min = 1,
  validCount,
  renderActions,
  instructions,
  maxSize = 10 * MB,
  accept = { 'image/*': ['.png', '.jpg', '.jpeg'], 'application/pdf': ['.pdf'] },
  disabled,
}) {
  const { t } = useTranslation()

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxSize,
    multiple: true,
    disabled,
    onDropAccepted: files => onAdd(files),
    onDropRejected: rejections =>
      snackbar.error(rejections.map(({ file, errors }) => `${file.name}: ${errors[0]?.message}`).join('; ')),
  })

  const missing = Math.max(min - (validCount ?? items.length), 0)

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{t('{{count}} file(s) attached', { count: items.length })}</Typography>
        <Typography variant="caption" color={missing ? 'error.main' : 'success.main'}>
          {missing ? t('{{count}} more required', { count: missing }) : t('Minimum reached')}
        </Typography>
      </Stack>

      {instructions && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mb: 1.5 }}>
          {instructions}
        </Typography>
      )}

      <Stack spacing={1}>
        {items.map(item => (
          <Stack
            key={item.key}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: item.flag?.color === 'error' ? 'error.light' : 'divider',
            }}
          >
            <DescriptionIcon color={item.flag?.color === 'error' ? 'error' : 'primary'} fontSize="small" />
            <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, minWidth: 0 }} title={item.name}>
              {item.name || t('Uploaded file')}
            </Typography>
            {item.flag && <Chip size="small" label={t(item.flag.label)} color={item.flag.color ?? 'default'} />}
            {item.uuid && (
              <Button size="small" onClick={() => openFile(item.uuid)}>
                {t('View')}
              </Button>
            )}
            {renderActions?.(item)}
            {!disabled && item.removable !== false && (
              <Tooltip title={t('Remove')}>
                <IconButton size="small" onClick={() => onRemove(item)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}

        {!disabled && (
          <Box
            {...getRootProps()}
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.light' },
            })}
          >
            <input {...getInputProps()} />
            <AddIcon fontSize="small" />
            <Typography variant="body2">
              {isDragActive ? t('Drop the files here') : t('Click to attach files or drag them here')}
            </Typography>
            <Typography variant="caption" sx={{ ml: 'auto' }}>
              PNG, JPG, PDF · {t('max')} {Math.round(maxSize / MB)}MB
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
